import { type ESocialNetwork } from '@mahawi/eshop-common/src/types';
import { Button, Select, Space, Tag, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import HistoryTable from '../components/facebook/history-table';
import ProductPickerModal from '../components/facebook/product-picker-modal';
import SuggestionCard from '../components/facebook/suggestion-card';
import { getCountryOptions } from '../fragments/get-country-options';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { countriesLoadAll } from '../reducers/countries/reducer';
import {
  facebookActiveNetworksLoad,
  facebookHistoryLoad,
  facebookScoredProductsLoad,
  facebookSuggestionLoad,
  facebookSuggestionPublish,
  facebookSuggestionSkip,
} from '../reducers/facebook/reducer';
import { SPACING } from '../theme/tokens';

const NETWORK_LABELS: Record<number, string> = {
  1: 'Facebook',
  2: 'Instagram',
};

function PostSuggestionsContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const countriesCountries = useSelector(
    (s: RootState) => s.Countries.countries,
  );
  const facebookActiveNetworks = useSelector(
    (s: RootState) => s.Facebook.activeNetworks,
  );
  const facebookProductsInProcess = useSelector(
    (s: RootState) => s.Facebook.productsInProcess,
  );
  const facebookInProcess = useSelector((s: RootState) => s.Facebook.inProcess);
  const facebookScoredProducts = useSelector(
    (s: RootState) => s.Facebook.scoredProducts,
  );
  const facebookSuggestion = useSelector(
    (s: RootState) => s.Facebook.suggestion,
  );
  const facebookHistory = useSelector((s: RootState) => s.Facebook.history);
  const facebookHistoryInProcess = useSelector(
    (s: RootState) => s.Facebook.historyInProcess,
  );

  const [selectedCountry, setSelectedCountry] = useState<string | undefined>(
    undefined,
  );
  const [selectedNetwork, setSelectedNetwork] = useState<
    ESocialNetwork | undefined
  >(undefined);
  const [selectedProductUUID, setSelectedProductUUID] = useState<
    string | undefined
  >(undefined);
  const [selectedProductName, setSelectedProductName] = useState<
    string | undefined
  >(undefined);
  const [pickerVisible, setPickerVisible] = useState<boolean>(false);

  useEffect((): void => {
    if (!countriesCountries) {
      dispatch(countriesLoadAll());
    }
    dispatch(facebookActiveNetworksLoad());
    dispatch(facebookHistoryLoad());
  }, [countriesCountries, dispatch]);

  useEffect((): void => {
    if (facebookActiveNetworks.length > 0 && selectedNetwork === undefined) {
      setSelectedNetwork(facebookActiveNetworks[0]);
    }
  }, [facebookActiveNetworks, selectedNetwork]);

  useEffect((): void => {
    if (!selectedCountry) {
      return;
    }
    dispatch(facebookScoredProductsLoad(selectedCountry));
  }, [selectedCountry, dispatch]);

  const handleGenerate = useCallback((): void => {
    if (selectedCountry && selectedNetwork !== undefined) {
      dispatch(
        facebookSuggestionLoad(
          selectedCountry,
          selectedNetwork,
          selectedProductUUID,
        ),
      );
    }
  }, [selectedCountry, selectedNetwork, selectedProductUUID, dispatch]);

  const handlePublish = useCallback(
    (
      id: number,
      text: string,
      socialNetwork: ESocialNetwork,
      productUUID: string,
      country?: string,
    ): void => {
      const effectiveCountry: string | undefined = country ?? selectedCountry;
      if (effectiveCountry) {
        dispatch(
          facebookSuggestionPublish(
            id,
            text,
            effectiveCountry,
            socialNetwork,
            productUUID,
          ),
        );
      }
    },
    [selectedCountry, dispatch],
  );

  const handleSkip = useCallback(
    (id: number): void => {
      dispatch(facebookSuggestionSkip(id));
    },
    [dispatch],
  );

  const handleProductSelect = useCallback(
    (uuid: string, name: string): void => {
      setSelectedProductUUID(uuid);
      setSelectedProductName(name);
      setPickerVisible(false);
    },
    [],
  );

  return (
    <Space orientation="vertical" size={SPACING.xl} style={{ width: '100%' }}>
      <DetailPageHeader title="Post suggestions" />

      <Space wrap>
        <Select
          placeholder="Select country"
          style={{ width: '13rem' }}
          value={selectedCountry}
          onChange={(value: string): void => {
            setSelectedCountry(value);
            setSelectedProductUUID(undefined);
            setSelectedProductName(undefined);
          }}
          options={getCountryOptions(countriesCountries)}
        />

        <Select
          placeholder="Select network"
          style={{ width: '10rem' }}
          value={selectedNetwork}
          onChange={(value: ESocialNetwork): void => setSelectedNetwork(value)}
          disabled={facebookActiveNetworks.length === 0}
          options={facebookActiveNetworks.map((network: ESocialNetwork) => ({
            value: network,
            label: NETWORK_LABELS[network] ?? String(network),
          }))}
        />

        <Button
          onClick={(): void => setPickerVisible(true)}
          loading={facebookProductsInProcess}
          disabled={!selectedCountry || facebookProductsInProcess}
        >
          {selectedProductName ? 'Change product' : 'Select product'}
        </Button>

        {selectedProductName ? (
          <Tag
            closable
            onClose={(): void => {
              setSelectedProductUUID(undefined);
              setSelectedProductName(undefined);
            }}
            style={{ padding: '3px 8px' }}
          >
            {selectedProductName}
          </Tag>
        ) : (
          <Typography.Text type="secondary">
            No product selected — backend picks best scored
          </Typography.Text>
        )}

        <Button
          type="primary"
          disabled={
            !selectedCountry ||
            selectedNetwork === undefined ||
            facebookInProcess
          }
          loading={facebookInProcess}
          onClick={handleGenerate}
        >
          Generate suggestion
        </Button>
      </Space>

      <ProductPickerModal
        visible={pickerVisible}
        scoredProducts={facebookScoredProducts}
        inProcess={facebookProductsInProcess}
        onSelect={handleProductSelect}
        onCancel={(): void => setPickerVisible(false)}
      />

      {selectedCountry && (
        <SuggestionCard
          suggestion={facebookSuggestion}
          inProcess={facebookInProcess}
          onPublish={handlePublish}
          onSkip={handleSkip}
        />
      )}

      <HistoryTable
        history={facebookHistory}
        countries={countriesCountries ?? []}
        loading={facebookHistoryInProcess}
        onPublish={handlePublish}
        onSkip={handleSkip}
      />
    </Space>
  );
}

export default PostSuggestionsContainer;
